/**
 * CompanyUsersDashboard Component
 * 
 * Dashboard for managing company users using the generic DataDashboard system.
 * 
 * @component
 * @example
 * <CompanyUsersDashboard
 *   user={adminUser}
 *   currentRoute="/dashboard/companies/123/users"
 *   onNavigate={handleNavigate}
 *   onLogout={handleLogout}
 * />
 */

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import DataDashboard from '../../../common/DataDashboard';
import { AdminUser } from '../../../../types/admin-v2';
import { API_ENDPOINTS } from '../../../../utils/config';
import { useAuth } from '../../../../context/AuthContext';
import AddUserModal from '../../modals/AddUserModal';
import { getDynamicSubItems } from '../../../../utils/getDynamicSubItems';
import CompanySubNavigation from '../../../common/CompanySubNavigation';

interface CompanyUsersDashboardProps {
  /** Current admin user */
  user: AdminUser;
  /** Current route */
  currentRoute: string;
  /** Navigation handler */
  onNavigate: (route: string) => void;
  /** Logout handler */
  onLogout: () => void;
}

const CompanyUsersDashboard: React.FC<CompanyUsersDashboardProps> = ({
  user,
  currentRoute,
  onNavigate,
  onLogout
}) => {
  const { companyId } = useParams<{ companyId: string }>();
  const { token } = useAuth();
  const [users, setUsers] = useState<any[]>([]);
  const [roles, setRoles] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [paginationInfo, setPaginationInfo] = useState({
    page: 1,
    limit: 25,
    total: 0,
    pages: 0,
    has_next: false,
    has_prev: false
  });

  // Load company roles for dropdown
  const loadRoles = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/v2/companies/${companyId}/roles`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setRoles(data.data || []);
        }
      }
    } catch (error) {
      console.error('Error loading roles:', error);
    }
  };



  // Define table columns for users (matching the image layout)
  const userColumns: any[] = [
    { key: 'user_id', title: 'User ID', type: 'text' as const, sortable: true, editable: false, width: '100px' },
    { key: 'name', title: 'Name', type: 'text' as const, sortable: true, editable: true, width: '150px' },
    {
      key: 'role_name',
      title: 'Role',
      type: 'text' as const,
      sortable: true,
      editable: false,  // Custom dropdown, not standard input
      width: '150px',
      render: (value: any, row: any) => {
        if (!isEditing) {
          return value;
        }
        // Render dropdown in edit mode
        return (
          <select
            value={row.role || ''}
            onChange={(e) => handleEditUser(row.id, 'role', e.target.value, row)}
            style={{
              padding: '4px 8px',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              fontSize: '13px',
              width: '100%'
            }}
          >
            {roles.map((role) => (
              <option key={role._id} value={role._id}>
                {role.name}
              </option>
            ))}
          </select>
        );
      }
    },
    { key: 'email', title: 'Email', type: 'text' as const, sortable: true, editable: true, width: '200px' },
    { key: 'total_activity', title: 'Total Activity', type: 'number' as const, sortable: true, editable: false, width: '120px' },
    { key: 'revision_requests', title: 'Revision Requests', type: 'text' as const, sortable: true, editable: false, width: '140px' },
    { key: 'approval', title: 'Approval', type: 'text' as const, sortable: true, editable: false, width: '120px' },
    { key: 'weekly_activity', title: 'Weekly Activity', type: 'text' as const, sortable: true, editable: false, width: '150px' }
  ];

  // Load users data
  const loadUsers = async (page = 1, limit = 25, search = '', sortBy = 'name', sortOrder = 'asc', columnFilters: Record<string, string> = {}) => {
    setIsLoading(true);
    try {
      // Build query params
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        search: search,
        sortBy: sortBy,
        sortOrder: sortOrder
      });

      // Add column filters
      Object.entries(columnFilters).forEach(([key, value]) => {
        if (value && value.trim()) {
          params.append(`filter_${key}`, value.trim());
        }
      });

      const response = await fetch(`${API_ENDPOINTS.COMPANIES}/${companyId}/users?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        // Transform users to include id field
        const transformedUsers = (data.data || []).map((user: any) => ({
          ...user,
          id: user._id  // Add id field for DataDashboard
        }));

        setUsers(transformedUsers);
        setPaginationInfo({
          page: data.pagination?.page || 1,
          limit: data.pagination?.limit || 25,
          total: data.pagination?.total || 0,
          pages: data.pagination?.totalPages || 0,
          has_next: data.pagination?.has_next || false,
          has_prev: data.pagination?.has_prev || false
        });
      } else {
        throw new Error(data.message || 'Failed to load users');
      }
    } catch (error: any) {
      console.error('Error loading users:', error);
      setError(error.message || 'Failed to load users');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle data fetch from DataDashboard
  const handleDataFetch = (params: { page: number; limit: number; search?: string; sortBy?: string; sortOrder?: string; columnFilters?: Record<string, string> }) => {
    loadUsers(params.page, params.limit, params.search || '', params.sortBy || 'name', params.sortOrder || 'asc', params.columnFilters || {});
  };

  // Handle edit toggle
  const handleEditToggle = () => {
    setIsEditing(!isEditing);
  };

  // Handle edit user
  const handleEditUser = async (id: string, field: string, value: any, row?: any) => {
    try {
      console.log('🔧 EDIT USER - id:', id, 'field:', field, 'value:', value);

      // Prepare update data
      const updateData: any = {};

      if (field === 'role') {
        // Role field - update with ObjectId
        updateData.role = value;
      } else {
        // Other fields (name, email)
        updateData[field] = value;
      }

      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/v2/companies/${companyId}/users/${id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });

      if (!response.ok) {
        throw new Error('Failed to update user');
      }

      const result = await response.json();

      if (result.success) {
        // Reload users to get fresh data from backend (includes joined role_name)
        await loadUsers(
          paginationInfo.page,
          paginationInfo.limit,
          '',
          'name',
          'asc',
          {}
        );

        console.log('✅ User updated successfully');
      }
    } catch (error) {
      console.error('Error editing user:', error);
    }
  };

  // Handle delete users (removed - delete button hidden)
  const handleDeleteUsers = (ids: string[]) => {
    // Delete functionality removed per user request
  };

  // Handle export
  const handleExport = async (exportData: any) => {
    try {
      if (!token || !companyId) {
        throw new Error('No authentication token or company ID found');
      }

      console.log('🚀 CompanyUsersDashboard handleExport called with:', exportData);

      // Company-specific users export için filters'ı düzenle
      // Backend'de users collection'ında company_id field'ı var
      // Company users için company_id ile filtrelemeliyiz

      const exportPayload = {
        ...exportData,
        filters: {
          // Company'ye özel users'ları filtrele
          company_id: companyId,
          // Eğer seçili item'lar varsa, onları da filtrele
          ...(exportData.filters?._id?.$in && exportData.filters._id.$in.length > 0
            ? { _id: exportData.filters._id }
            : {})
        }
      };

      console.log('🚀 CompanyUsersDashboard export payload:', exportPayload);

      // ExportModal'dan gelen data yapısını kullan, company_id filtresi ile
      const response = await fetch(API_ENDPOINTS.EXPORT_DATA, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(exportPayload)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('🚀 CompanyUsersDashboard export error response:', errorText);
        throw new Error(`Export failed: ${response.status} ${errorText}`);
      }

      // Handle file download
      const blob = await response.blob();
      console.log('🚀 CompanyUsersDashboard export blob size:', blob.size);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      const today = new Date();
      const dateStr = `${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, '0')}${String(today.getDate()).padStart(2, '0')}`;
      const baseFilename = exportData.filename || `company_${companyId}_users`;
      a.download = `${baseFilename}_${dateStr}.${exportData.format === 'excel' ? 'xlsx' : 'csv'}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      console.log('🚀 CompanyUsersDashboard export completed successfully');
    } catch (error) {
      console.error('🚀 CompanyUsersDashboard export error:', error);
      alert(`Failed to export users: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  // Handle add user
  const handleAddUser = () => {
    setShowAddUserModal(true);
  };

  // Handle user created successfully
  const handleUserCreated = async () => {
    // Reload users list
    await loadUsers(
      paginationInfo.page,
      paginationInfo.limit,
      '',
      'name',
      'asc',
      {}
    );
  };

  // Load initial data
  useEffect(() => {
    if (companyId && token) {
      loadRoles();
      loadUsers();
    }
  }, [companyId, token]);

  // Only show secondary sidebar for admin users, not for regular users
  const isAdmin = user.role === 'admin';

  return (
    <>
      <DataDashboard
        title="Company Users"
        pageDescription="Manage team members and access levels"
        collection="users"
        data={users}
        columns={userColumns}
        user={user}
        currentRoute={currentRoute}
        onNavigate={onNavigate}
        onLogout={onLogout}
        onDataChange={setUsers}
        onDataFetch={handleDataFetch}
        onEdit={handleEditUser}
        onEditToggle={handleEditToggle}
        isEditing={isEditing}
        onExport={handleExport}
        paginationInfo={paginationInfo}
        showAddButton={true}
        showDeleteButton={false}
        showSearch={true}
        showSelection={true}
        layoutType={isAdmin ? 'admin' : 'user'}
        onAdd={handleAddUser}
        getDynamicSubItems={getDynamicSubItems}
        navigationActions={
          <CompanySubNavigation
            currentRoute={currentRoute}
            onNavigate={onNavigate}
            isAdmin={isAdmin}
          />
        }
      />

      {/* Add User Modal */}
      {companyId && (
        <AddUserModal
          isOpen={showAddUserModal}
          onClose={() => setShowAddUserModal(false)}
          onSuccess={handleUserCreated}
          companyId={companyId}
          roles={roles}
        />
      )}
    </>
  );
};

export default CompanyUsersDashboard;